// Base Imports
import { useCallback, useEffect, useState } from 'react'

// Package Imports
import axios from '@services/axiosInterceptorInstance'
import Router, { useRouter } from 'next/router'

// Component Imports
import { useUI } from '@components/ui/context'
import useWishlist from '@components/services/wishlist'
import cartHandler from '@components/services/cart'
import useAnalytics from '@components/services/analytics/useAnalytics'
import { EVENTS_MAP } from '@components/services/analytics/constants'
import Cookies from 'js-cookie'

// Other Imports
import {
  fetchUser,
  getCurrentPage,
  getKwikPassFields,
  resetSubmitData,
  setDefaultPaymentMethod,
  setDeliveryAddress,
  submitData,
} from '@framework/utils/app-util'
import { LocalStorage } from '@components/utils/payment-constants'
import { IExtraProps } from '@components/common/Layout/Layout'
import { GA_EVENT } from 'hooks/ga_event.modal'
import {
  AppsFlyerKeys,
  AppsFlyerParams,
  EmptyGuid,
  FromValues,
  LoginPageAction,
  NEXT_AUTHENTICATE,
  NEXT_GET_ORDERS,
  NEXT_OTP_REQUEST,
  OneDayOrderLimit,
  MonthOrderLimit,
  SevenDayOrderLimit,
  exceptionNumber,
  blockedNumber,
} from '@components/utils/constants'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useGoogleAnalyticsEvent } from 'hooks/useGAEvent'
import { getSHA256Hash, getEncryptedNumber } from '@framework/utils/cipher'
import { asyncHandler } from './Address/helper'
import {
  INewLoginProps,
  LOGIN_FORM_ID,
  OTP_TIMER,
  calculateLastOrderRecords,
  getUserNumber,
  handleOTPGTMEvent,
} from './loginHelper'
import LoginModalDialogBox from './LoginModalDialogBox'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { PPG_MAX_LIMIT } from '@framework/utils/constants'
import { getDeviceTypeString } from '@commerce/utils/use-device'
import { v4 as uuid } from 'uuid'

declare const window: any
declare const AF: any

const PAGE_SIZE = 10

const LoginModal = (props: INewLoginProps & IExtraProps) => {
  const { submitState, submitDispatch, show, close, previousPath, deviceInfo } =
    props
  const router = useRouter()
  const isChannelStore = router?.query?.channelStore ? true : false
  const [isOTPSent, setIsOTPSent] = useState(false)
  const [mobileNumber, setMobileNumber] = useState<any>()
  const [isError, showError] = useState(false)
  const [timer, setTimer] = useState(0)

  const {
    isGuestUser,
    setIsGuestUser,
    setUser,
    user,
    wishListItems,
    setCartItems,
    setBasketId,
    storeBasketId,
    setWishlist,
    cartItems,
    basketId,
    openPPGErrorModal,
    setShowPreparedOrder,
    setShowLoaderInModal,
    openPPGBlockedModal,
  } = useUI()
  const { getWishlist } = useWishlist()
  const { associateCart, getCartByUser } = cartHandler()
  const { PageViewed } = EVENTS_MAP.EVENT_TYPES
  const { asPath } = router
  const { getAddress } = asyncHandler()
  const trackGoogleAnalyticsEvent = useGoogleAnalyticsEvent()
  const trackMoEngageEvent = useMoEngageEvent()
  const { isMobile, isIPadorTablet } = deviceInfo
  useAnalytics(PageViewed, {
    eventType: PageViewed,
  })

  const LOGIN_FORM_FIELDS = [
    {
      type: 'tel',
      name: 'mobileNumber',
      placeholder: 'Mobile Number',
      label: 'Mobile Number',
      className:
        'relative mb-2 mt-2 appearance-none min-w-0 w-full bg-white border border-gray-300 rounded-sm shadow-sm py-2 px-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black focus:ring-1 focus:ring-black',
      labelClassName: 'text-gray-700 text-sm dark:text-black',
      required: true,
      disabled: false,
      max: 10,
      handleChange: (e: any, item: any, context: any) => {
        const regex = /^[0-9\s]*$/
        if (regex.test(e?.target.value.toString())) {
          context.setFieldValue(LOGIN_FORM_FIELDS[0]?.name, e.target.value)
        }
        if (isChannelStore) {
          if (e?.target?.value?.length === 10) {
            handleLogin({ mobileNumber: e?.target?.value, otp: null })
          }
        }
      },
    },
    {
      type: 'tel',
      name: 'otp',
      placeholder: 'OTP',
      label: 'OTP',
      className:
        'relative mb-2 mt-2 appearance-none min-w-0 w-full bg-white border border-gray-300 rounded-sm shadow-sm py-2 px-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black focus:ring-1 focus:ring-black',
      labelClassName: 'text-gray-700 text-sm dark:text-black',
      required: false,
      disabled: false,
      max: 6,
      handleChange: (e: any, item: any, context: any) => {
        const regex = /^[0-9\s]*$/
        if (regex.test(e?.target.value.toString())) {
          context.setFieldValue(LOGIN_FORM_FIELDS[1]?.name, e.target.value)
        }
        if (isChannelStore) {
          if (e?.target?.value?.length === 6) {
            handleLogin({ mobileNumber: mobileNumber, otp: e?.target?.value })
          }
        }
      },
    },
  ]

  const handleLogin = async (data: any) => {
    try {
      const previousPage = getCurrentPage(previousPath)
      if (!isOTPSent) {
        if (isChannelStore) {
          trackGoogleAnalyticsEvent(
            GA_EVENT.PPG_OTP_REQUEST,
            {
              previous_page: previousPage,
              loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
            },
            window
          )
        } else {
          const mobileRSA = getEncryptedNumber(data?.mobileNumber || mobileNumber)
          const gaPayload = {
            mobile_number : mobileRSA,
            previous_page: previousPage,
            address_source: 'Damensch',
            ...getKwikPassFields(),
          }
          recordGA4Event(window, GA_EVENT.OTP_REQUEST, gaPayload)
        }

        resetTimer()
        submitData(submitDispatch, LoginPageAction.SEND_OTP)
        const otpData = {
          mobileNo: data?.mobileNumber || mobileNumber,
          entityType: 1,
          templateId: 1,
          isChannelStore: isChannelStore ? true : false,
        }
        const { data: otpResponse }: any = await axios.post(
          NEXT_OTP_REQUEST,
          otpData
        )
        setIsOTPSent(otpResponse?.success)

        if (otpResponse?.success) {
          handleOTPGTMEvent()
          setMobileNumber(data?.mobileNumber || mobileNumber)
          setTimer(OTP_TIMER)
        }

        resetSubmitData(submitDispatch)
      } else {
        if (isChannelStore) {
          trackGoogleAnalyticsEvent(
            GA_EVENT.PPG_OTP_SUBMIT,
            {
              previous_page: previousPage,
              loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
            },
            window
          )
        } else {
          const gaPayload = {
            previous_page: previousPage,
            address_source: 'Damensch',
            ...getKwikPassFields(),
          }
          recordGA4Event(window, GA_EVENT.OTP_SUBMIT, gaPayload)
        }

        submitData(submitDispatch, LoginPageAction.LOGIN)
        await handleUserLogin(data, cartItems)
      }
    } catch (error) {
      resetSubmitData(submitDispatch)
      console.error('err', error)
    }
  }

  const resendOTP = async (data: any) => {
    resetTimer()
    submitData(submitDispatch, LoginPageAction.SEND_OTP)
    const otpData = {
      mobileNo: data?.mobileNumber || mobileNumber,
      entityType: 1,
      templateId: 1,
      isChannelStore: isChannelStore ? true : false,
    }
    const { data: otpResponse }: any = await axios.post(
      NEXT_OTP_REQUEST,
      otpData
    )
    setIsOTPSent(otpResponse?.success)
    recordGA4Event(window, GA_EVENT.RESEND_OTP, {
      page_path: asPath,
    })
    if (otpResponse?.success) {
      handleOTPGTMEvent()
      setMobileNumber(data?.mobileNumber || mobileNumber)
    }

    resetSubmitData(submitDispatch)
  }

  const trackMoengageLoginEvent = (mobileNumber: string) => {
    if(!mobileNumber) return
    trackMoEngageEvent(
      MO_ENGAGE_EVENT.LOGIN,
      {
        URL: router?.asPath || '',
        device: getDeviceTypeString(),
        first_login:'',
        status: "sent",
        mobile_number: mobileNumber,
        uuid : uuid()
      },
      window
    )
  }

  const handleUserLogin = async (values: any, cartItems: any) => {
    // If previous guest login exists
    if (isGuestUser) {
      if (user && !user?.userId) {
        localStorage.removeItem('user')
      }
      localStorage.removeItem(LocalStorage.Key.ORDER_RESPONSE)
      localStorage.removeItem(LocalStorage.Key.ORDER_PAYMENT)
      localStorage.removeItem(LocalStorage.Key.CONVERTED_ORDER)
      localStorage.removeItem(LocalStorage.Key.DELIVERY_ADDRESS)
      localStorage.removeItem(LocalStorage.Key.PREFERRED_PAYMENT)
    }

    const data = {
      username: values?.mobileNumber,
      password: values?.otp,
      isOTPBasedAuthentication: true,
      isChannelStore: isChannelStore ? true : false,
    }
    const { data: loginResult }: any = await axios.post(NEXT_AUTHENTICATE, {
      data: data,
    })

    localStorage.setItem(
      LocalStorage.Key.userEncryptedDetails,
      loginResult?.userToken?.access_token
    )
    Cookies.set(
      LocalStorage.Key.userEncryptedDetails,
      loginResult?.userToken?.access_token
    )

    if (loginResult && isChannelStore) {
      //this case is only for channel store

      const userObj = { ...loginResult }
      trackMoengageLoginEvent(userObj?.mobile)
      const userId = loginResult?.userId
      const newCart = await associateCart(userId, storeBasketId, isChannelStore)
      setIsGuestUser(false)

      const getCustomerResult = await fetchUser(
        userId,
        isChannelStore ? true : false
      )

      setDefaultPaymentMethod(getCustomerResult)

      const addressResult: any = await getAddress(
        loginResult?.userId,
        isChannelStore ? true : false
      )
      const shippingAddress = addressResult.find(
        (item: any) => item?.isDefaultDelivery
      )
      if (shippingAddress) {
        setDeliveryAddress(shippingAddress)
      }
      setUser(userObj)
      close()
      resetSubmitData(submitDispatch)
      // set moengage user attributes
      if (window?.Moengage) {
        window?.Moengage.update_unique_user_id(userObj.mobile)
        window?.Moengage.add_unique_user_id(userObj.mobile)
        window?.Moengage.add_first_name(userObj.firstName)
        window?.Moengage.add_last_name(userObj.lastName)
        window?.Moengage.add_email(userObj.email)
        window?.Moengage.add_mobile(userObj.mobile)
        window?.Moengage.add_user_name(
          `${userObj.firstName} ${userObj.lastName}`
        )
        window?.Moengage.add_gender(userObj.gender)
        window?.Moengage.add_birthday(
          `${userObj.dayOfBirth}/${userObj.monthOfBirth}/${userObj.yearOfBirth}`
        )
        window?.Moengage.add_user_attribute('address_source', 'Damensch')
      }
      const checkoutUrl = '/checkout'
      if (
        window?.location?.hash &&
        window?.location?.hash === `#${FromValues.CHECKOUT}`
      ) {
        Router.push(checkoutUrl)
      } else if (!isChannelStore) {
        Router.reload()
      }
      if (
        userObj?.userId &&
        userObj?.userId !== EmptyGuid &&
        cartItems?.lineItems?.length > 0
      ) {
        setTimeout(() => {
          fetchOrders(userObj)
        }, 800)
      }
    } else if (
      !loginResult ||
      (loginResult && loginResult?.userId === EmptyGuid)
    ) {
      resetSubmitData(submitDispatch)
      showError(true)
    } else if (loginResult?.userId) {
      const previousPage = getCurrentPage()
      const mobileRSA = getEncryptedNumber(loginResult?.username || getUserNumber(loginResult))
      const gaPayload = {
        previous_page: previousPage,
        address_source: 'Damensch',
        mobile_number: mobileRSA,
        ...getKwikPassFields(),
      }
      recordGA4Event(window, GA_EVENT.OTP_SUBMIT, gaPayload)
      trackMoengageLoginEvent(values?.mobileNumber)
      if (typeof AF !== 'undefined') {
        const mobileNumber =
          loginResult?.mobile || loginResult?.telephone || loginResult?.username
        let mobile256Sha = await getSHA256Hash(mobileNumber)
        AF('pba', 'setCustomerUserId', mobile256Sha)
        AF('pba', 'event', {
          eventType: 'EVENT',
          eventName: AppsFlyerKeys.Login,
          eventRevenueCurrency: 'INR',
          eventValue: {
            [AppsFlyerParams.AFEventParamCustomerUserId]: mobile256Sha,
            [AppsFlyerParams.AFEventParamContentType]: 'Online store',
            [AppsFlyerParams.AFEventParamCurrency]: 'INR',
            [AppsFlyerParams.AFEventParam2]: mobileNumber ?? '',
            [AppsFlyerParams.AFEventParam1]: loginResult?.userId ? true : false,
          },
        })
      }

      const userObj = { ...loginResult }
      const wishlist = await getWishlist(loginResult?.userId, wishListItems)
      setWishlist(wishlist)
      getWishlist(loginResult?.userId, wishListItems)

      const userId = loginResult?.userId
      const newCart = await associateCart(userId, basketId)
      setCartItems(newCart?.data)
      setIsGuestUser(false)

      const getCustomerResult = await fetchUser(userId)
      setDefaultPaymentMethod(getCustomerResult)

      let cart: any = {}
      if (cartItems?.userId && cartItems?.userId === EmptyGuid) {
        cart = await associateCart(loginResult?.userId, cartItems?.id)
      } else {
        cart = await getCartByUser({
          userId: loginResult?.userId,
          cart: cartItems,
          basketId,
        })
      }

      const addressResult: any = await getAddress(
        loginResult?.userId,
        isChannelStore ? true : false
      )
      const shippingAddress = addressResult.find(
        (item: any) => item?.isDefaultDelivery
      )
      if (shippingAddress) {
        setDeliveryAddress(shippingAddress)
      }
      if (cart?.id) {
        setCartItems(cart)
        setBasketId(cart?.id)
        userObj.isAssociated = true
      } else {
        userObj.isAssociated = false
      }
      setUser(userObj)
      close()
      resetSubmitData(submitDispatch)

      // set moengage user attributes
      if (window?.Moengage) {
        window?.Moengage.update_unique_user_id(values?.mobileNumber)
        window?.Moengage.add_unique_user_id(values?.mobileNumber)
        window?.Moengage.add_first_name(userObj.firstName)
        window?.Moengage.add_last_name(userObj.lastName)
        window?.Moengage.add_email(userObj.email)
        window?.Moengage.add_mobile(values?.mobileNumber)
        window?.Moengage.add_user_name(
          `${userObj.firstName} ${userObj.lastName}`
        )
        window?.Moengage.add_gender(userObj.gender)
        window?.Moengage.add_birthday(
          `${userObj.dayOfBirth}/${userObj.monthOfBirth}/${userObj.yearOfBirth}`
        )
        window?.Moengage.add_user_attribute('address_source', 'Damensch')
      }
      const checkoutUrl = '/checkout'
      if (
        window?.location?.hash &&
        window?.location?.hash === `#${FromValues.CHECKOUT}`
      ) {
        Router.push(checkoutUrl)
      } else if (!isChannelStore) {
        Router.reload()
      }
    }
  }

  const fetchOrders = async (user: any) => {
    setShowLoaderInModal(true)
    const addressInfo: any = Cookies?.get('storeDetails')
    const storeJson = addressInfo && JSON.parse(addressInfo)
    const userMobileNum = user.mobile || user.telephone || user.username
    const orderLimit = PPG_MAX_LIMIT || 9999999999999999
    if (
      (+orderLimit < cartItems?.grandTotal?.raw?.withTax ||
        blockedNumber?.includes(userMobileNum) ||
        storeJson?.phoneNo?.toLowerCase()?.includes(userMobileNum)) &&
      !exceptionNumber?.includes(userMobileNum)
    ) {
      setShowLoaderInModal(false)
      openPPGBlockedModal()
    } else {
      const { data: ordersResult }: any = await axios.post(NEXT_GET_ORDERS, {
        id: user?.userId,
        hasMembership: user?.hasMembership,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      })
      const orderCount = calculateLastOrderRecords(ordersResult)
      if (
        (orderCount?.countTodayOrder >= OneDayOrderLimit ||
          orderCount?.countWeekOrder >= SevenDayOrderLimit ||
          orderCount?.countMonthOrder >= MonthOrderLimit) &&
        !exceptionNumber?.includes(user?.username)
      ) {
        setShowLoaderInModal(false)
        openPPGErrorModal()
      } else {
        setShowLoaderInModal(false)
        setShowPreparedOrder(true)
      }
    }
  }

  useEffect(() => {
    setIsOTPSent(false)
  }, [show])

  useEffect(() => {
    setTimeout(() => showError(false), 5000)
  }, [isError])

  const timeOutCallback = useCallback(
    () => setTimer((currTimer) => currTimer - 1),
    []
  )

  useEffect(() => {
    if (timer > 0) {
      const otpTimer = setTimeout(timeOutCallback, 1000)
      return () => {
        clearTimeout(otpTimer)
      }
    }
  }, [timer, timeOutCallback])

  const resetTimer = function () {
    if (!timer) {
      setTimer(OTP_TIMER)
    }
  }

  if (typeof document !== 'undefined') {
    if (
      document !== undefined &&
      document.getElementById(`otp-${LOGIN_FORM_ID}`)
    ) {
      document.getElementById(`otp-${LOGIN_FORM_ID}`)?.focus()
    }

    if (
      document !== undefined &&
      document.getElementById(`mobileNumber-${LOGIN_FORM_ID}`)
    ) {
      document.getElementById(`mobileNumber-${LOGIN_FORM_ID}`)?.focus()
    }
  }

  if (typeof window === 'undefined') {
    return null
  }

  return (
    <LoginModalDialogBox
      isChannelStore={isChannelStore}
      isOTPSent={isOTPSent}
      setIsOTPSent={setIsOTPSent}
      setTimer={setTimer}
      mobileNumber={mobileNumber}
      timer={timer}
      resendOTP={resendOTP}
      LOGIN_FORM_FIELDS={LOGIN_FORM_FIELDS}
      handleLogin={handleLogin}
      submitState={submitState}
      isError={isError}
      show={show}
      submitDispatch={submitDispatch}
    />
  )
}
export default LoginModal
